/**
 * Location Select Dialog for Transfer
 * Matches AddInventoryStepper location dialog: Table with Location + Action (Select button), primary location at top
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, TextField, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, Button, IconButton,
  Typography, useTheme, alpha
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';

export default function LocationSelectDialog({
  open,
  onClose,
  title,
  locations,
  searchTerm,
  onSearchChange,
  onSelect,
  excludeLocationId = null
}) {
  const theme = useTheme();

  const filteredAndSortedLocations = React.useMemo(() => {
    const filtered = locations.filter(loc => {
      const matchesSearch = (loc.name || '').toLowerCase().includes((searchTerm || '').toLowerCase());
      const notExcluded = !excludeLocationId || loc.id !== excludeLocationId;
      return matchesSearch && notExcluded;
    });
    return [...filtered].sort((a, b) => {
      const aPrimary = !!(a.is_main || a.is_primary);
      const bPrimary = !!(b.is_main || b.is_primary);
      if (aPrimary && !bPrimary) return -1;
      if (!aPrimary && bPrimary) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [locations, searchTerm, excludeLocationId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '60vh',
          width: '80vw',
          maxWidth: '1200px'
        }
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }
      }}
    >
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 3,
        pt: 3,
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`
      }}>
        <Typography variant="h5" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 4 }}>
        <TextField
          fullWidth
          placeholder="Search locations..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{
            mt: 3,
            mb: 4,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '1rem'
            }
          }}
        />
        <Table sx={{
          '& .MuiTableCell-root': {
            fontSize: '1rem',
            py: 2.5
          }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 500 }}>Location</TableCell>
              <TableCell align="right" sx={{ fontWeight: 500 }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedLocations.map((loc) => (
              <TableRow
                key={loc.id}
                hover
                sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
              >
                <TableCell sx={{ fontSize: '0.95rem' }}>
                  {loc.name}
                </TableCell>
                <TableCell align="right">
                  <Button
                    variant="outlined"
                    onClick={() => onSelect(loc)}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1,
                      fontSize: '0.95rem'
                    }}
                  >
                    Select
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
}
